import React, { useEffect } from 'react';
import { Controller, Control, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import type { WorkModelPreference } from '@/features/Collaborators/Models/collaborator.types';

import { PositionCombobox, SeniorityCombobox, StaticCombobox, RequiredIndicator, FileUploadInput } from '@/components/shared';
import { usePositions } from '@/hooks/usePositions';
import { useSeniorities } from '@/hooks/useSeniorities';
import { formatCnpj } from '@/utils/form-validation/brazilian-documents';
import { activityStatusOptions, normalizeActivityStatus } from '@/utils/collaborator-status';
import { TerminationFields } from './components/TerminationFields';

export interface StaticOption {
    value: string;
    label: string;
}

interface HiringFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId?: string;
    control: Control<any>; // Control do React Hook Form do formulário pai
    setValue: UseFormSetValue<any>; // SetValue do React Hook Form do formulário pai
    watch: UseFormWatch<any>; // Watch do React Hook Form do formulário pai
    errors?: Record<string, any>; // Erros do formulário pai
    isSubmitting?: boolean; // Estado de submissão via props
    layout?: 'full' | 'compact'; // Layout via props
    uploadStates?: Record<string, { progress?: number; status?: 'idle' | 'uploading' | 'success' | 'error' }>;
    onInternshipContractUpload?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// Opções estáticas ordenadas alfabeticamente - usando valores corretos do schema
const collaboratorTypeOptions: StaticOption[] = [
    { value: 'Estagiário', label: 'Estagiário' },
    { value: 'Celetista', label: 'Funcionário' },
    { value: 'Fornecedor', label: 'Fornecedor' },
    { value: 'Sócio', label: 'Sócio' }
];

const roleTypeOptions: StaticOption[] = [
    { value: 'administrative', label: 'Administrativo' },
    { value: 'operational', label: 'Operacional' }
];

const workModelPreferenceOptions: Array<{ value: WorkModelPreference; label: string }> = [
    { value: 'Remote', label: 'Remoto' },
    { value: 'Hybrid', label: 'Híbrido' },
    { value: 'Onsite', label: 'Presencial' },
];

const HiringFieldset: React.FC<HiringFieldsetProps> = ({
    context,
    collaboratorId,
    control,
    setValue,
    watch,
    errors = {},
    isSubmitting = false,
    layout = 'full',
    uploadStates = {},
    onInternshipContractUpload
}) => {
    // Hooks para dados dos comboboxes
    const { positions, loading: positionsLoading, error: positionsError } = usePositions();
    const { seniorities, loading: senioritiesLoading, error: senioritiesError } = useSeniorities();
    // Adicionar estilos CSS para animação
    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
                @keyframes fadeSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                        max-height: 0;
                        padding-top: 0;
                        margin-top: 0;
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                        max-height: 500px;
                        padding-top: 1rem;
                        margin-top: 1rem;
                    }
                }
                
                @keyframes fadeSlideOut {
                    from {
                        opacity: 1;
                        transform: translateY(0);
                        max-height: 500px;
                        padding-top: 1rem;
                        margin-top: 1rem;
                    }
                    to {
                        opacity: 0;
                        transform: translateY(-10px);
                        max-height: 0;
                        padding-top: 0;
                        margin-top: 0;
                    }
                }
            `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Watch para campos condicionais
    const formData = watch();
    const startDate = watch('startDate');
    const collaboratorType = watch('collaboratorType');
    const positionId = watch('positionId');
    const seniorityId = watch('seniorityId');
    const preferredWorkModels: WorkModelPreference[] = watch('preferredWorkModels') ?? [];

    // Estado para controlar a animação da seção de estágio
    const [showInternshipSection, setShowInternshipSection] = React.useState(collaboratorType === 'Estagiário');
    const [animatingOut, setAnimatingOut] = React.useState(false);

    // Controlar a exibição da seção de estágio com delay para animação
    React.useEffect(() => {
        if (collaboratorType === 'Estagiário') {
            setAnimatingOut(false);
            setShowInternshipSection(true);
        } else if (showInternshipSection) {
            setAnimatingOut(true);
            const timer = setTimeout(() => {
                setShowInternshipSection(false);
                setAnimatingOut(false);
            }, 500); // Delay para animação de saída
            return () => clearTimeout(timer);
        }
    }, [collaboratorType, showInternshipSection]);

    const handleToggleWorkModelPreference = (value: WorkModelPreference) => {
        const current = new Set(preferredWorkModels);
        if (current.has(value)) {
            current.delete(value);
        } else {
            current.add(value);
        }
        setValue('preferredWorkModels', Array.from(current), {
            shouldDirty: true,
            shouldTouch: true,
        });
    };

    // Helper para verificar se campo deve aparecer usando helper global
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Helper local para verificar se campos são obrigatórios
    const isRequired = (fieldName: string) => collaboratorSchemaHelpers.isFieldRequired(fieldName, context);

    // useEffects para atualizar campos dependentes
    useEffect(() => {
        const currentPosition = positions.find(p => p.id === positionId);
        if (currentPosition) {
            setValue('positionName', currentPosition.name);
        }
    }, [positionId, positions, setValue]);

    useEffect(() => {
        const currentSeniority = seniorities.find(s => s.id === seniorityId);
        if (currentSeniority) {
            setValue('seniorityName', currentSeniority.name);
        }
    }, [seniorityId, seniorities, setValue]);

    useEffect(() => {
        if (startDate && collaboratorType === 'Celetista') {
            const start = new Date(startDate);
            const experienceEnd = new Date(start);
            experienceEnd.setDate(experienceEnd.getDate() + 90);
            const experienceEndFormatted = experienceEnd.toISOString().split('T')[0];
            setValue('experienceEndDate', experienceEndFormatted);
        } else if (collaboratorType !== 'Celetista') {
            setValue('experienceEndDate', '');
        }
    }, [startDate, collaboratorType, setValue]);

    return (
        <fieldset className="border p-4 rounded-md">
            <legend className="text-sm font-medium px-1 text-gray-400">Dados de Contratação</legend>
            <div className="space-y-4 pt-2">

                {/* Primeira linha: Tipo de Colaborador, Cargo e Senioridade */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {shouldShow('collaboratorType') && (
                        <div>
                            <label htmlFor="collaboratorType" className="block text-sm font-medium text-gray-700">
                                Tipo de Colaborador <RequiredIndicator show={isRequired('collaboratorType')} />
                            </label>
                            <Controller
                                name="collaboratorType"
                                control={control}
                                render={({ field }) => (
                                    <StaticCombobox
                                        options={collaboratorTypeOptions}
                                        selectedValue={field.value}
                                        onChange={(value) => field.onChange(value || '')}
                                        disabled={isSubmitting}
                                        placeholder="Selecione o tipo"
                                    />
                                )}
                            />
                            {errors.collaboratorType && (
                                <p className="mt-1 text-sm text-red-600">{errors.collaboratorType.message}</p>
                            )}
                        </div>
                    )}

                    {shouldShow('positionId') && (
                        <div>
                            <label htmlFor="position" className="block text-sm font-medium text-gray-700">
                                Cargo <RequiredIndicator show={isRequired('positionId')} />
                            </label>
                            <Controller
                                name="positionId"
                                control={control}
                                render={({ field }) => (
                                    <PositionCombobox
                                        selectedId={field.value}
                                        onChange={(position) => {
                                            field.onChange(position?.id || '');
                                        }}
                                        disabled={isSubmitting}
                                        positions={positions}
                                        loading={positionsLoading}
                                        error={positionsError}
                                    />
                                )}
                            />
                            {errors.positionId && (
                                <p className="mt-1 text-sm text-red-600">{errors.positionId.message}</p>
                            )}
                        </div>
                    )}

                    {shouldShow('seniorityId') && (
                        <div>
                            <label htmlFor="seniority" className="block text-sm font-medium text-gray-700">
                                Senioridade <RequiredIndicator show={isRequired('seniorityId')} />
                            </label>
                            <Controller
                                name="seniorityId"
                                control={control}
                                render={({ field }) => (
                                    <SeniorityCombobox
                                        selectedId={field.value}
                                        onChange={(seniority) => {
                                            field.onChange(seniority?.id || '');
                                        }}
                                        disabled={isSubmitting}
                                        seniorities={seniorities}
                                        loading={senioritiesLoading}
                                        error={senioritiesError}
                                    />
                                )}
                            />
                            {errors.seniorityId && (
                                <p className="mt-1 text-sm text-red-600">{errors.seniorityId.message}</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Segunda linha: Data de Início, Data de Término e Status */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {shouldShow('startDate') && (
                        <div>
                            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                                Data de Início <RequiredIndicator show={isRequired('startDate')} />
                            </label>
                            <Controller
                                name="startDate"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        value={field.value || ''}
                                        type="date"
                                        id="startDate"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                    />
                                )}
                            />
                            {errors.startDate && (
                                <p className="mt-1 text-sm text-red-600">{errors.startDate.message}</p>
                            )}
                        </div>
                    )}

                    {shouldShow('endDate') && (
                        <div>
                            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                                Data de Término
                            </label>
                            <Controller
                                name="endDate"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        value={field.value || ''}
                                        type="date"
                                        id="endDate"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                    />
                                )}
                            />
                            {errors.endDate && (
                                <p className="mt-1 text-sm text-red-600">{errors.endDate.message}</p>
                            )}
                        </div>
                    )}

                    {shouldShow('status') && (
                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                                Status <RequiredIndicator show={isRequired('status')} />
                            </label>
                            <Controller
                                name="status"
                                control={control}
                                render={({ field }) => (
                                    <StaticCombobox
                                        options={activityStatusOptions}
                                        selectedValue={normalizeActivityStatus(field.value)}
                                        onChange={(value) => field.onChange(value || '')}
                                        disabled={isSubmitting}
                                        placeholder="Selecione o status"
                                    />
                                )}
                            />
                            {errors.status && (
                                <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                            )}
                        </div>
                    )}

                    {/* Data de término de experiência - só para funcionário, readonly */}
                    {shouldShow('experienceEndDate') && collaboratorType === 'Celetista' && (
                        <div>
                            <label htmlFor="experienceEndDate" className="block text-sm font-medium text-gray-700">
                                Final do Período de Experiência
                            </label>
                            <Controller
                                name="experienceEndDate"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        value={field.value || ''}
                                        type="date"
                                        id="experienceEndDate"
                                        className="input-primary w-full mt-1 bg-gray-50"
                                        disabled={true}
                                        readOnly={true}
                                    />
                                )}
                            />
                            <p className="mt-1 text-xs text-gray-500">Calculado automaticamente (90 dias após início)</p>
                        </div>
                    )}
                </div>

                {context === 'FULL_FORM' && (
                    <TerminationFields
                        collaboratorId={collaboratorId}
                        control={control}
                        watch={watch}
                        isSubmitting={isSubmitting}
                    />
                )}

                {shouldShow('preferredWorkModels') && (
                    <div className="space-y-3">
                        <span className="block text-sm font-medium text-gray-700">
                            Preferências de regime de trabalho
                        </span>
                        <div className="flex flex-wrap gap-4">
                            {workModelPreferenceOptions.map(({ value, label }) => {
                                const checkboxId = `preferred-work-model-${value.toLowerCase()}`;
                                return (
                                    <label
                                        key={value}
                                        htmlFor={checkboxId}
                                        className="inline-flex items-center gap-2 text-sm text-gray-700"
                                    >
                                        <input
                                            id={checkboxId}
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            checked={preferredWorkModels.includes(value)}
                                            onChange={() => handleToggleWorkModelPreference(value)}
                                            disabled={isSubmitting}
                                        />
                                        {label}
                                    </label>
                                );
                            })}
                        </div>
                        <p className="text-xs text-gray-500">
                            Se nenhuma opção for selecionada, consideramos o colaborador disponível para qualquer regime.
                        </p>
                        {errors.preferredWorkModels && (
                            <p className="text-xs text-red-600">{errors.preferredWorkModels.message}</p>
                        )}
                    </div>
                )}

                {/* Terceira linha: Tipo de Função, Carga Horária e Ex-aluno Raro Academy */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {shouldShow('roleType') && (
                        <div>
                            <label htmlFor="roleType" className="block text-sm font-medium text-gray-700">
                                Tipo de Função <RequiredIndicator show={isRequired('roleType')} />
                            </label>
                            <Controller
                                name="roleType"
                                control={control}
                                render={({ field }) => (
                                    <StaticCombobox
                                        options={roleTypeOptions}
                                        selectedValue={field.value}
                                        onChange={(value) => field.onChange(value || '')}
                                        disabled={isSubmitting}
                                        placeholder="Selecione o tipo"
                                    />
                                )}
                            />
                            {errors.roleType && (
                                <p className="mt-1 text-sm text-red-600">{errors.roleType.message}</p>
                            )}
                        </div>
                    )}

                    {shouldShow('workSchedule') && (
                        <div>
                            <label htmlFor="workSchedule" className="block text-sm font-medium text-gray-700">
                                Carga Horária (horas) <RequiredIndicator show={isRequired('workSchedule')} />
                            </label>
                            <Controller
                                name="workSchedule"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        value={field.value || ''}
                                        type="number"
                                        id="workSchedule"
                                        placeholder="Ex: 8"
                                        min="1"
                                        max="24"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Converter para number ou undefined se vazio
                                            field.onChange(value ? parseInt(value, 10) : undefined);
                                        }}
                                    />
                                )}
                            />
                            {errors.workSchedule && (
                                <p className="mt-1 text-sm text-red-600">{errors.workSchedule.message}</p>
                            )}
                        </div>
                    )}

                    {shouldShow('isRaroAcademyAlumnus') && (
                        <div className="flex items-center pt-6">
                            <Controller
                                name="isRaroAcademyAlumnus"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        type="checkbox"
                                        id="isRaroAcademyAlumnus"
                                        checked={field.value || false}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        disabled={isSubmitting}
                                    />
                                )}
                            />
                            <label htmlFor="isRaroAcademyAlumnus" className="ml-2 block text-sm text-gray-900">
                                Ex-aluno Raro Academy 🎓
                            </label>
                        </div>
                    )}
                </div>

                {/* Quarta linha: Nome da Empresa e CNPJ para fornecedor */}
                {collaboratorType === 'Fornecedor' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {shouldShow('companyName') && (
                            <div>
                                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                                    Nome da Empresa <RequiredIndicator show={isRequired('companyName')} />
                                </label>
                                <Controller
                                    name="companyName"
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            value={field.value || ''}
                                            type="text"
                                            id="companyName"
                                            placeholder="Nome da empresa"
                                            className="input-primary w-full mt-1"
                                            disabled={isSubmitting}
                                        />
                                    )}
                                />
                                {errors.companyName && (
                                    <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>
                                )}
                            </div>
                        )}

                        {shouldShow('cnpj') && (
                            <div>
                                <label htmlFor="cnpj" className="block text-sm font-medium text-gray-700">
                                    CNPJ <RequiredIndicator show={isRequired('cnpj')} />
                                </label>
                                <Controller
                                    name="cnpj"
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            type="text"
                                            id="cnpj"
                                            placeholder="00.000.000/0000-00"
                                            className="input-primary w-full mt-1"
                                            disabled={isSubmitting}
                                            onChange={(e) => {
                                                const formatted = formatCnpj(e.target.value);
                                                field.onChange(formatted);
                                            }}
                                            value={field.value || ''}
                                        />
                                    )}
                                />
                                {errors.cnpj && (
                                    <p className="mt-1 text-sm text-red-600">{errors.cnpj.message}</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Campos de Estágio - aparecem apenas se for estagiário */}
                {showInternshipSection && (
                    <div
                        className="border-t overflow-hidden"
                        style={{
                            animation: animatingOut
                                ? 'fadeSlideOut 0.5s ease-in-out forwards'
                                : 'fadeSlideIn 0.5s ease-in-out forwards'
                        }}
                    >
                        <h4 className="text-md font-medium text-gray-900 mb-4">Dados do Estágio</h4>

                        {/* Linha com os 3 campos de estágio + upload do contrato */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {shouldShow('internshipEducationalInstitution') && (
                                <div>
                                    <label htmlFor="internshipEducationalInstitution" className="block text-sm font-medium text-gray-700">
                                        Instituição de Ensino <RequiredIndicator show={isRequired('internshipEducationalInstitution')} />
                                    </label>
                                    <Controller
                                        name="internshipEducationalInstitution"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                value={field.value || ''}
                                                type="text"
                                                id="internshipEducationalInstitution"
                                                className="input-primary w-full mt-1"
                                                disabled={isSubmitting}
                                            />
                                        )}
                                    />
                                    {errors.internshipEducationalInstitution && (
                                        <p className="mt-1 text-sm text-red-600">{errors.internshipEducationalInstitution.message}</p>
                                    )}
                                </div>
                            )}

                            {shouldShow('internshipCourse') && (
                                <div>
                                    <label htmlFor="internshipCourse" className="block text-sm font-medium text-gray-700">
                                        Curso <RequiredIndicator show={isRequired('internshipCourse')} />
                                    </label>
                                    <Controller
                                        name="internshipCourse"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                {...field}
                                                value={field.value || ''}
                                                type="text"
                                                id="internshipCourse"
                                                className="input-primary w-full mt-1"
                                                disabled={isSubmitting}
                                            />
                                        )}
                                    />
                                    {errors.internshipCourse && (
                                        <p className="mt-1 text-sm text-red-600">{errors.internshipCourse.message}</p>
                                    )}
                                </div>
                            )}

                            {/* Upload do contrato de estágio na terceira coluna */}
                            {shouldShow('internshipContractFile') && (
                                <div>
                                    <Controller
                                        name="internshipContractFile"
                                        control={control}
                                        render={({ field }) => (
                                            <FileUploadInput
                                                name="internshipContractFile"
                                                label="Contrato de Estágio"
                                                fileUrl={formData.internshipContractUrl}
                                                fileName={formData.internshipContractFile?.name}
                                                isSubmitting={isSubmitting}
                                                onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;

                                                    field.onChange(file);

                                                    // Se há handler personalizado, usar ele
                                                    if (onInternshipContractUpload) {
                                                        onInternshipContractUpload(e);
                                                    }
                                                }}
                                                progress={uploadStates['internshipContractFile']?.progress}
                                                status={uploadStates['internshipContractFile']?.status}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default HiringFieldset;
